import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from '../local-storage.service';
import { LoggerService } from '../logger/logger.service';
import { ApiColCollaboratorOut } from '../rest-api/rest-api';
import { isConsultant } from './guards';
import { Router } from '@angular/router';
import { SignalsService } from '../signals.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _loggedConsult: ApiColCollaboratorOut | null = null;
  private _bearer = '';

  public asyncUserDataUpdating = new BehaviorSubject<boolean>(true);
  public hasAnyPortfolioOrStrategy = true;

  public refreshTokenUpdating(): Observable<boolean> {
    return this.asyncUserDataUpdating.asObservable();
  }

  public get bearer(): string {
    return this._bearer;
  }

  public get loggedConsult(): ApiColCollaboratorOut | null {
    return this._loggedConsult;
  }

  public set loggedConsult(value: ApiColCollaboratorOut | null) {
    this._loggedConsult = value;
    this.saveConsultToLocalStorage();
  }

  public constructor(
    private localStorageService: LocalStorageService,
    private loggerService: LoggerService,
    private router: Router,
    private signalsService: SignalsService,
  ) {
    this.handleInitData();
    window.addEventListener('storage', this.storageChanged.bind(this));
  }

  private handleInitData(): void {
    const bearer = this.localStorageService.getItemSync({key: LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY}) ?? '';
    const userData = this.localStorageService.getItemSync({key: LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY});

    this._bearer = bearer;
    try {
      if(userData && !this._loggedConsult) {
        this._loggedConsult = JSON.parse(userData);
      }
    } catch (error) {
      this.loggerService.error('Failed to parse local data. ', error);
    }
    this.asyncUserDataUpdating.next(false);
  }

  public removeUserFromLocal(): void {
    this.loggedConsult = null;
    this._bearer = '';
    this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.TOKEN_LOCAL_STORAGE_KEY });
    this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.TOKEN_EXPIRES_LOCAL_STORAGE_KEY });
    this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.LOCAL_STORAGE_TIMER_KEY });
    this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY });
    this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY });
  }

  private saveConsultToLocalStorage(): void {
    if (this.loggedConsult) {
      this.localStorageService.setItem({
        key: LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY,
        value: JSON.stringify(this._loggedConsult)
      });
      this.signalsService.triggerUserUpdate();
    }
  }

  public updateBearer(newToken: string): void {
    this._bearer = newToken;
    this.localStorageService.setItem({
      key: LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY,
      value: newToken,
    });
  }

  private storageChanged(event: StorageEvent): void {
    if(
      (event.key === LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY || event.key === LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY) &&
      typeof event.newValue === 'string' &&
      event.newValue.includes('{')
    ) {
      const storageBearerToken = this.localStorageService.getItemSync({ key: LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY });

      // if attempted to redirect without Bearer would throw 401 and logout both windows
      if(!storageBearerToken || storageBearerToken === null) return;

      try {
        const parsedEventValue = JSON.parse(event.newValue);
        const consultant = isConsultant(parsedEventValue);

        if (consultant) {
          this._loggedConsult = consultant;
          this._bearer = storageBearerToken;
          this.router.navigate(['/workplace-summary'], { replaceUrl: true });
        }
      } catch (error) {
        console.error('Invalid object from local data.');
        this.loggerService.error(error);
      }
    }
  }
}
